/**
 * Authentication Configuration Module
 * 
 * Centralized auth state management with workspace bootstrap.
 * Exposes only authenticated and authLoading state.
 * 
 * Per PHASE_B_WORK_ORDERS.md B4 & B6
 */

import { useState, useEffect } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { bootstrapWorkspace } from '@threadlock/data';
import { setWorkspaceState } from './workspace';
import { auth, db } from '../firebase-init'; // Import from firebase-init to avoid circular deps

export interface AuthState {
  authenticated: boolean;
  authLoading: boolean;
  emailVerified: boolean;
  user: User | null;
}

let cachedAuthState: AuthState = {
  authenticated: false,
  authLoading: true,
  emailVerified: false,
  user: null,
};

const listeners = new Set<(state: AuthState) => void>();

function notifyListeners() {
  listeners.forEach((listener) => listener(cachedAuthState));
}

// Initialize auth state listener with workspace bootstrap
onAuthStateChanged(auth, async (user) => {
  if (user) {
    // User authenticated - bootstrap workspace
    try {
      setWorkspaceState({ workspaceLoading: true, hasAccess: false, activeWorkspaceId: null });
      
      const workspace = await bootstrapWorkspace(db, user.uid);
      
      // Set workspace state
      setWorkspaceState({
        activeWorkspaceId: workspace.id,
        hasAccess: true,
        workspaceLoading: false,
      });
    } catch (error) {
      console.error('[Auth] Workspace bootstrap failed:', error);
      setWorkspaceState({
        activeWorkspaceId: null,
        hasAccess: false,
        workspaceLoading: false,
      });
    }
  } else {
    // User logged out - clear workspace
    setWorkspaceState({
      activeWorkspaceId: null,
      hasAccess: false,
      workspaceLoading: false,
    });
  }
  
  cachedAuthState = {
    authenticated: !!user,
    authLoading: false,
    emailVerified: user?.emailVerified || false,
    user,
  };
  notifyListeners();
});

/**
 * Hook to access auth state
 */
export function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>(cachedAuthState);

  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}

/**
 * Get current auth state synchronously
 */
export function getAuthState(): AuthState {
  return cachedAuthState;
}
